import React from 'react';
import { X, AlertTriangle, Upload, Plus, Wine, FileText } from 'lucide-react';
interface EditLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  productSku: string;
  productName: string;
}
export function EditLabelModal({
  isOpen,
  onClose,
  productSku,
  productName
}: EditLabelModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-248">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-249">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-250">
          <div data-id="element-251">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-252">
              Edit Product Label
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-253">
              Update label elements for TTB compliance
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-254">
            <X className="w-5 h-5" data-id="element-255" />
          </button>
        </div>
        <div className="p-6" data-id="element-256">
          <form className="space-y-6" data-id="element-257">
            {/* Product Info */}
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-258">
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-259">
                <div data-id="element-260">
                  <span className="text-gray-600" data-id="element-261">Product SKU:</span>
                  <span className="ml-2 font-medium text-gray-900" data-id="element-262">
                    {productSku}
                  </span>
                </div>
                <div data-id="element-263">
                  <span className="text-gray-600" data-id="element-264">Product Name:</span>
                  <span className="ml-2 font-medium text-gray-900" data-id="element-265">
                    {productName}
                  </span>
                </div>
              </div>
            </div>
            {/* Brand Name */}
            <div data-id="element-266">
              <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-267">
                Brand Name*
              </label>
              <input id="brand" type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" defaultValue="Sonoma Valley Vineyards" data-id="element-268" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-269">
                Must match registered trade name with TTB
              </p>
            </div>
            {/* Class/Type Designation */}
            <div data-id="element-270">
              <label htmlFor="class" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-271">
                Class/Type Designation*
              </label>
              <input id="class" type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" defaultValue="Cabernet Sauvignon" data-id="element-272" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-273">
                Must contain at least 75% of named grape variety
              </p>
            </div>
            {/* Appellation */}
            <div data-id="element-274">
              <label htmlFor="appellation" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-275">
                Appellation of Origin*
              </label>
              <input id="appellation" type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" defaultValue="Alexander Valley, Sonoma County, California" data-id="element-276" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-277">
                At least 85% of grapes must be grown in stated appellation
              </p>
            </div>
            {/* Alcohol Content */}
            <div data-id="element-278">
              <label htmlFor="alcohol" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-279">
                Alcohol Content*
              </label>
              <div className="flex items-center" data-id="element-280">
                <input id="alcohol" type="text" className="w-24 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" defaultValue="14.5" data-id="element-281" />
                <span className="ml-2" data-id="element-282">% ALC/VOL</span>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-283">
                Must be within 0.5% of actual alcohol content
              </p>
            </div>
            {/* Estate Designation */}
            <div data-id="element-284">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-285">
                Estate Designation
              </label>
              <div className="space-y-2" data-id="element-286">
                <div className="flex items-center" data-id="element-287">
                  <input type="checkbox" id="estate" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" defaultChecked data-id="element-288" />
                  <label htmlFor="estate" className="ml-2 block text-sm text-gray-900" data-id="element-289">
                    "Estate Reserve" designation
                  </label>
                </div>
              </div>
              <p className="text-xs text-red-500 mt-1" data-id="element-290">
                <AlertTriangle className="w-3 h-3 inline mr-1" data-id="element-291" />
                Requires 100% estate grown grapes. Verification needed.
              </p>
            </div>
            {/* Upload Label Artwork */}
            <div data-id="element-292">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-293">
                Label Artwork
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6" data-id="element-294">
                <div className="flex flex-col items-center" data-id="element-295">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" data-id="element-296" />
                  <p className="text-sm text-gray-600 text-center mb-2" data-id="element-297">
                    Upload new label artwork or revisions
                  </p>
                  <button type="button" className="text-purple-600 text-sm font-medium" data-id="element-298">
                    Browse Files
                  </button>
                </div>
              </div>
            </div>
            {/* TTB Warning */}
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg" data-id="element-299">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-300" />
              <div className="text-sm text-yellow-600" data-id="element-301">
                <p className="font-medium mb-1" data-id="element-302">TTB Compliance Notice</p>
                <p data-id="element-303">
                  All changes to approved labels may require a new COLA
                  (Certificate of Label Approval) submission to the TTB.
                  Allowable revisions without new approval are limited.
                </p>
              </div>
            </div>
          </form>
        </div>
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200" data-id="element-304">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:text-gray-900" data-id="element-305">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2" data-id="element-306">
            Save Changes
          </button>
        </div>
      </div>
    </div>;
}